import { useMemo } from "react";

import { usePortalData } from "@/components/portal/PortalDataProvider";
import type { OrganisationRole } from "@/types/portal";

export type PermissionAction =
  | "view"
  | "create"
  | "edit"
  | "delete"
  | "manage"
  | "execute";

export type PermissionResource =
  | "billing"
  | "subscriptions"
  | "team"
  | "settings"
  | "rbac"
  | "groups"
  | "products"
  | "connectors"
  | "reporting"
  | "spaces"
  | "automation"
  | "documents"
  | "tables"
  | "marketing"
  | "theme"
  | "notifications"
  | "api-keys"
  // Self-service per-user MCP keys (scope `mcp:query`). RBAC-only — CP derives
  // it from `platform_features: mcp-keys` in config/services.yaml, so
  // `can("create", "mcp-keys")` resolves for org_owner/admin/member.
  | "mcp-keys"
  // The OAuth grants Claude holds (cp `platform_features: claude-connections`).
  // org_owner/admin/member all hold it — revoking only REDUCES access — and it
  // is NOT subscription-gated, so a grant can be ended after Connect lapses.
  | "claude-connections"
  | "kb-file"
  | "kb-sql"
  // Claude Connect (cp `config/services.yaml` `claude_connect`, feature_key
  // `claude-connect`): all-false without an ACTIVE claude_connect
  // subscription, else the same Casbin answer as kb-sql. A Connect-only org
  // holds this and not `kb-sql` — pages both subscriptions share OR the two.
  | "claude-connect"
  | "ftp"
  | "expense"
  | "messaging"
  | "hr"
  | "lms"
  | "vpn"
  // Managed database (paid service): self-service Azure SQL firewall
  // whitelisting. Gate the UI on `create` — org_partner holds `view` through
  // the broad `/organisations/*` read row, but never `create`.
  | "database-access";

export type UsePermissionsReturn = {
  /** Current user's role in the selected organisation */
  role: OrganisationRole | undefined;
  /** Check if user is org_owner */
  isOwner: boolean;
  /** Check if user is org_admin or higher */
  isAdmin: boolean;
  /** Check if user is org_member */
  isMember: boolean;
  /** Check if user can perform an action on a resource (Casbin RBAC) */
  can: (action: PermissionAction, resource: PermissionResource) => boolean;
  /** Check if user has any of the specified roles */
  hasRole: (...roles: OrganisationRole[]) => boolean;
  /** Whether user bypasses group checks (org_owner, org_admin, platform_admin) */
  bypassesGroups: boolean;
  /** Larasoft platform staff. Distinct from isAdmin/bypassesGroups; used to
   *  exempt platform admins from org-level guardrails (e.g. the org-admin daily
   *  schedule-frequency cap). */
  isPlatformAdmin: boolean;
  /** True until the backend RBAC answer has SETTLED — i.e. `bypassesGroups`,
   *  `can`, `role` etc. still hold their pre-fetch defaults (which lean to the
   *  deny side). Sourced from `permissions === null` (the provider's own
   *  "not resolved yet" sentinel — it becomes `{}` even on error, never back
   *  to null), NOT from `usePortalData().loading`, which only covers the
   *  earlier ORGANISATIONS fetch and is already `false` throughout the RBAC
   *  round-trip. A gate that would DENY on a pre-fetch default (e.g.
   *  `!bypassesGroups`) must hold off while this is true, or it flashes a
   *  false denial to a user who will resolve as an admin. */
  rbacResolving: boolean;
  /** True when the RBAC fetch FAILED, as opposed to having answered "denied".
   *
   *  `rbacResolving` cannot express this: the provider settles `permissions` to
   *  `{}` on error and never back to `null`, so a failed fetch is indistinguishable
   *  from a legitimate all-deny answer once resolving is over. Anything that would
   *  take a destructive or unrecoverable action on a denial — consuming a one-shot
   *  URL param, for instance — must check this too, or a transient RBAC outage
   *  reads as a permanent "you may not". */
  rbacFailed: boolean;
};

/**
 * Hook to check user permissions based on backend Casbin RBAC.
 *
 * Permissions are fetched from `GET /api/v1/rbac/my-permissions` by
 * PortalDataProvider when the selected organisation changes.
 * While loading, all permissions default to `false` (deny).
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { can, isAdmin, role } = usePermissions();
 *
 *   if (!can("edit", "billing")) {
 *     return <p>You don't have permission to edit billing</p>;
 *   }
 *
 *   return <BillingEditor />;
 * }
 * ```
 */
export function usePermissions(): UsePermissionsReturn {
  const {
    selectedOrganisation,
    permissions,
    permissionsFailed,
    bypassesGroups,
    isPlatformAdmin,
  } = usePortalData();

  return useMemo(() => {
    const role = selectedOrganisation?.role;

    const isOwner = role === "org_owner";
    const isAdmin = role === "org_owner" || role === "org_admin";
    const isMember = role === "org_member";

    const can = (action: PermissionAction, resource: PermissionResource): boolean => {
      // Deny while permissions are still loading from backend
      if (!permissions) return false;
      return permissions[resource]?.[action] ?? false;
    };

    const hasRole = (...roles: OrganisationRole[]): boolean => {
      if (!role) return false;
      return roles.includes(role);
    };

    return {
      role,
      isOwner,
      isAdmin,
      isMember,
      can,
      hasRole,
      bypassesGroups,
      isPlatformAdmin,
      // `permissions === null` = RBAC not yet fetched (before an org is
      // selected, or mid-round-trip). It settles to a populated object on
      // success or `{}` on error — never back to null — so this is a true
      // one-way "resolving → resolved" latch.
      rbacResolving: permissions === null,
      rbacFailed: permissionsFailed,
    };
  }, [
    selectedOrganisation?.role,
    permissions,
    permissionsFailed,
    bypassesGroups,
    isPlatformAdmin,
  ]);
}
